import { Component, OnInit, OnChanges, SimpleChanges, Input, Output, EventEmitter } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import * as moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { MailDetailsCustomNatureDataDto } from 'src/app/shared/models/mails/mail-details-dto.';
import { ListItemsOfNatureCustomFields, NatureCustomFields } from 'src/app/shared/interfaces/natures/complementary-datas.interface';

interface FormValidators {
  min?: number;
  max?: number;
  required?: boolean;
  requiredTrue?: boolean;
  email?: boolean;
  minLength?: boolean;
  maxLength?: boolean;
  pattern?: string;
  nullValidator?: boolean;
}
interface FormControlOptions {
  min?: string;
  max?: string;
  step?: string;
  icon?: string;
}
interface FormControls {
  id: number;
  customFieldId: number;
  name: string;
  label: string;
  value;
  type: string;
  options?: FormControlOptions;
  required: boolean;
  validators: FormValidators;
  textValue: string;
  numberValue: number;
  decimalValue: number;
  dateValue: moment.Moment;
  natureId?: number;
  utilisable: boolean;
  listItemsDropdown?: ListItemsOfNatureCustomFields[];
  listValueItemId?: number;
  defaultFormValue?;
}
export interface FormData {
  controls: FormControls[];
}

export interface customDataForEmitter {
  id: number;
  natureCustomFieldId: number;
  textValue: string;
  numberValue: number;
  decimalValue;
  dateValue: string;
  natureId: number;
  type: string;
  label: string;
}
@Component({
  selector: 'app-complement-data-form',
  templateUrl: './complement-data-form.component.html',
  styleUrls: ['./complement-data-form.component.scss'],
})

//Component used to display the complement datas of a chosen nature
// Concerns edition and visualisation (read only)
export class ComplementDataFormComponent implements OnInit, OnChanges {
  @Input() formData: FormData; // For complementary datas to be modified => readonly === false
  @Input() formDataForReadonly: MailDetailsCustomNatureDataDto[]; // For readonly complementary datas

  public formDataTextarea: FormData;
  public formDataReadonlyTextarea: MailDetailsCustomNatureDataDto[] = []; // liste a part comprenant uniquement les données complémentaires dont le type est "Dropdown". En effet ils seront affichés a part dans l'html

  @Input() natureWithComplementaryDatas;

  @Input() readOnly = false;
  @Input() mode: 'create' | 'update'; // Used to patch or not the default value for each control. Create will only patch default value ; update will only patch mail infos
  @Input() isAdvancedSearch = false; // Displays the dropdown as a multiselect 'cause in advancedSearch the user should be able to choose multiple options
  public myForm: UntypedFormGroup = this.fb.group({});
  @Output() formValue = new EventEmitter<customDataForEmitter[]>();

  public dataIsReadyToBeDisplayed = false; // boolean displaying the html
  public dataIsReadyToBeDisplayedInReadonlyMode = false; // boolean displaying the html for readonly
  private readonlySourceDatas: MailDetailsCustomNatureDataDto[] = [];

  constructor(
    private fb: UntypedFormBuilder,
    public toastr: ToastrService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.formDataForReadonly) {
      this.readonlySourceDatas = changes.formDataForReadonly.currentValue ?? [];
    }
    const readonlyInputsChanged = changes.formDataForReadonly || changes.natureWithComplementaryDatas;
    if (this.readOnly && readonlyInputsChanged) {
      this.setFormValuesReadonly();
    }
  }

  ngOnInit(): void {
    this.dataIsReadyToBeDisplayed = false;
    if (this.readOnly === false) {
      this.createForm(this.formData.controls);
      // valueForm est de type key-value[] dont la value dépend de ce que contient le formulaire de natures complémentaires : string, number, boolean, date,...
      // En effet on a en Key le nom de la nature complémentaire et en value le contenu. Le contenu est donc lié au type de la key qui peut varier en fonction de la key elle-même
      this.myForm.valueChanges.subscribe({
        next: (valueForm) => {
          this.setFormValues(valueForm);
        },
      });
    }
  }
  createForm(controls: FormControls[]): void {
    this.formDataTextarea = { controls: [] };
    try {
      if (controls?.length > 0) {
        controls.forEach((control) => {
          // For each type we need to check multiple things
          // If it's a creation, we need to patch the defaultFormValue
          // If it's a modification,... We simply need to patch the control value
          let rightNatureCustomField: NatureCustomFields;
          // !! les types ont été toLowercase() dans courrier-edition-form
          switch (control.type) {
            case 'text':
              if (this.mode === 'create') {
                control.value = control.defaultFormValue ? control.defaultFormValue : null;
              } else {
                control.value = control.textValue ? control.textValue : null;
              }
              break;
            case 'date':
              //! There's no default date. Seen with KHA 24-02-25
              if (this.mode === 'create') {
                control.value = null;
              } else {
                control.value = control.dateValue ? moment(control.dateValue).format('DD/MM/YYYY') : null;
              }
              break;
            case 'checkbox':
              if (this.mode === 'create') {
                control.value = control.defaultFormValue === 'true' ? true : false;
              } else {
                control.value = control.numberValue ? true : false;
              }
              break;
            case 'decimal':
              if (this.mode === 'create') {
                control.value = control.defaultFormValue ? +control.defaultFormValue.toString().replace(/,/g, '.') : null;
              } else {
                control.value = control.decimalValue ? +control.decimalValue.toString().replace(/,/g, '.') : null;
              }
              break;
            case 'integer':
              if (this.mode === 'create') {
                control.value = control.defaultFormValue ? control.defaultFormValue : null;
              } else {
                control.value = control.numberValue ? control.numberValue : null;
              }
              break;
            case 'textarea':
              if (this.mode === 'create') {
                control.value = control.defaultFormValue ? control.defaultFormValue : null;
              } else {
                control.value = control.textValue ? control.textValue : null;
              }
              this.formDataTextarea.controls.push(control);
              break;
            case 'dropdown':
              // getting dropdown list datas
              rightNatureCustomField = this.natureWithComplementaryDatas.natureCustomFields.find(
                (customField) => customField.id === control.customFieldId
              );
              control.listItemsDropdown = rightNatureCustomField.listItems
                .filter((dropdownItem) => {
                  // Ici on va devoir filtrer sur "utilisable" MAIS AUSSI sur le fait que la valeur soit utilisée par la fiche courrier
                  // En effet si la valeur est utilisable : false MAIS utilisée par la fiche courrier => il FAUT l'afficher dans le dropdown

                  let valueToCheck = null;
                  if (!dropdownItem.utilisable) {
                    if (this.mode === 'update') {
                      if (control.listValueItemId) {
                        valueToCheck = control.listValueItemId;
                      } else valueToCheck = null;
                    }
                  }
                  return dropdownItem.utilisable || (valueToCheck && dropdownItem.id === valueToCheck);
                })
                .map((dropdownItem) => {
                  dropdownItem.label = dropdownItem.description ? dropdownItem.description : dropdownItem.name;
                  dropdownItem.value = dropdownItem.id;
                  return dropdownItem;
                });
              if (this.mode === 'create') {
                // ! dans la création, si le defaultFormValue n'existe pas dans la liste control.listItemsDropdown (=> qu'il est inutilisable) alors on ne le set pas
                if (control.defaultFormValue && control.listItemsDropdown.findIndex((item) => item.id === +control.defaultFormValue) > -1) {
                  control.value = +control.defaultFormValue;
                } else control.value = null;
              } else {
                control.value = control.listValueItemId ? control.listValueItemId : null;
              }
              break;
          }
          this.myForm.addControl(control.name, this.fb.control(control.value));
        });
        this.formData.controls = this.formData.controls.filter((control) => control.type !== 'textarea');
        // We need to call this.setFormValues to send directly the values to the parent component. The parent needs to receive directly the 1st values in case if the user never changes them
        this.setFormValues(this.myForm.value);
      }
      this.dataIsReadyToBeDisplayed = true;
    } catch (error) {
      console.error('Error : ', error);
    }
  }

  setFormValues(valueForm): void {
    const customData = [];
    this.formData.controls.forEach((control) => {
      for (const key in valueForm) {
        if (control.label === key) {
          customData.push({
            id: control.id ? control.id : null,
            natureCustomFieldId: control.customFieldId ? control.customFieldId : null,
            textValue: control.type === 'text' ? valueForm[key] : null,
            numberValue: control.type === 'checkbox' ? (valueForm[key] === true ? 1 : 0) : control.type === 'integer' ? valueForm[key] : null,
            decimalValue: control.type === 'decimal' ? valueForm[key] : null,
            dateValue:
              control.type === 'date'
                ? moment(valueForm[key]).isValid()
                  ? moment(valueForm[key]).format('YYYY-MM-DD')
                  : moment(valueForm[key], 'DD/MM/YYYY').isValid()
                    ? moment(valueForm[key], 'DD/MM/YYYY').format('YYYY-MM-DD')
                    : null
                : null,
            natureId: control.natureId ? control.natureId : null,
            type: control.type ? control.type : null,
            label: control.label,
            listValueItemId: control.type === 'dropdown' ? valueForm[key] : null,
          });
          //! le label ici au dessus ne sera utile pour l'instant que pour l'affichage des filtres de la recherche avancée dans le composant via le routing courriers/advancedSearch
        }
      }
    });
    this.formDataTextarea.controls.forEach((control) => {
      for (const key in valueForm) {
        if (control.label === key) {
          customData.push({
            id: control.id ? control.id : null,
            natureCustomFieldId: control.customFieldId ? control.customFieldId : null,
            textValue: valueForm[key],
            numberValue: null,
            decimalValue: null,
            dateValue: null,
            natureId: control.natureId ? control.natureId : null,
            type: control.type ? control.type : null,
            label: control.label,
            listValueItemId: null,
          });
        }
      }
    });
    try {
      if (customData && customData.length > 0) {
        this.formValue.emit(customData);
      }
    } catch (error) {
      this.toastr.error("Une erreur s'est produite lors de l'envoi des données complémentaires");
    }
  }

  setFormValuesReadonly(): void {
    this.formDataReadonlyTextarea = [];
    this.formDataForReadonly = this.readonlySourceDatas.map((nature) => {
      // Pour avancer plus loin, on doit vérifier si le type est dropdown.
      if (nature.type === 'Dropdown') {
        nature.dropdownValue = '';
        // Si on a un type dropdown, il va falloir trouver a quoi rime la valeur listValueItemId.
        // pour ce faire il faut trouver la natureCustomFields dans la liste des natureWithComplementaryDatas qui correspond a la nature de type dropdown que nous avons.
        // Une fois la bonne nature trouvée, on va rechercher dans listItems l'id correspondant au listValueItemId.
        const rightNatureCustomField = this.natureWithComplementaryDatas.natureCustomFields.find(
          (customField) => customField.id === nature.natureCustomFieldId
        );
        if (rightNatureCustomField) {
          const rightDataShow = rightNatureCustomField.listItems.find((item) => item.id === nature.listValueItemId);
          if (rightDataShow) {
            nature.dropdownValue = rightDataShow.name;
          }
        }
      }
      // If we have a textarea, then we push it into another form because in the html we need to loop through it into another row and it's easier to place it simply inside another var
      if (nature.type === 'Textarea' && nature.id !== null) {
        this.formDataReadonlyTextarea.push(nature);
      }
      return nature;
    });
    this.formDataForReadonly = this.formDataForReadonly.filter((nature) => nature.type !== 'Textarea' && nature.id !== null);
    this.dataIsReadyToBeDisplayedInReadonlyMode = true;
  }
}
